import { createHash } from "node:crypto";
import { lstat, readFile, readdir } from "node:fs/promises";
import path from "node:path";

/**
 * Digest a release artifact using the standard SHA-256 file digest. Directory targets use a
 * deterministic, tagged tree digest so deployment bundles can be certified without archiving them.
 */
export async function digestDatabricksArtifact(target: string): Promise<string> {
  const targetInfo = await lstat(target);
  rejectUnsupportedEntry(target, targetInfo);
  if (targetInfo.isFile()) {
    return createHash("sha256").update(await readFile(target)).digest("hex");
  }

  const hash = createHash("sha256");
  async function append(directory: string, relativeDirectory: string): Promise<void> {
    for (const name of (await readdir(directory)).sort()) {
      const candidate = path.join(directory, name);
      const relative = path.join(relativeDirectory, name).replaceAll(path.sep, "/");
      const info = await lstat(candidate);
      rejectUnsupportedEntry(relative, info);
      if (info.isDirectory()) {
        hash.update(`directory\0${relative}\0`);
        await append(candidate, relative);
      } else {
        const bytes = await readFile(candidate);
        hash.update(`file\0${relative}\0${bytes.byteLength}\0`);
        hash.update(bytes);
      }
    }
  }
  await append(target, "");
  return hash.digest("hex");
}

function rejectUnsupportedEntry(
  entry: string,
  info: Awaited<ReturnType<typeof lstat>>,
): void {
  if (info.isSymbolicLink()) {
    throw new Error(`Databricks artifact digest rejects symbolic links: ${entry}`);
  }
  if (!info.isFile() && !info.isDirectory()) {
    throw new Error(`Databricks artifact digest rejects non-file entries: ${entry}`);
  }
}
